import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, FileText, ListChecks, AlertTriangle } from 'lucide-react';
import { adminApi } from '../lib/api';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const Warnings = ({ v }) => (
  <div className="space-y-1 text-sm">
    {v.ambiguous_recipes.length > 0 && <p className="text-[#C86B51]" data-testid="qty-ambiguous"><AlertTriangle className="inline w-4 h-4 mr-1" />Recettes ambiguës (nom de tarte/pizza mais catégorie différente, calculées par personne) : {v.ambiguous_recipes.join(', ')}</p>}
    {v.unparsed.length > 0 && <p className="text-[#C86B51]" data-testid="qty-unparsed"><AlertTriangle className="inline w-4 h-4 mr-1" />Non comptés (à vérifier) : {v.unparsed.map((u) => `${u.recipe} — ${u.text}${u.warning ? ` (${u.warning})` : ''}`).join(' ; ')}</p>}
    {(v.anomalies || []).filter((a) => a.kind !== 'unparsed').map((a, i) => (
      <p key={i} className="text-[#C86B51]" data-testid={`qty-anomaly-${i}`}><AlertTriangle className="inline w-4 h-4 mr-1" />{a.recipe} — {a.text} : {a.warning}</p>
    ))}
  </div>
);

const ValidationTable = ({ v }) => (
  <div className="space-y-4 max-h-[70vh] overflow-y-auto" data-testid="qty-validation">
    <Warnings v={v} />
    <table className="w-full text-sm">
      <thead><tr className="text-left text-[#5C6A61] border-b"><th className="py-1 pr-2">Recette</th><th className="pr-2">Ingrédient</th><th className="pr-2">Donnée d'origine</th><th className="pr-2">Quantité lue</th><th className="pr-2">Coef.</th><th>Résultat</th></tr></thead>
      <tbody>
        {v.rows.map((r, i) => (
          <tr key={i} className="border-b border-[#E3DED5]">
            <td className="py-1 pr-2">{r.recipe}{r.mode === 'preparation_entiere' ? ' (préparation entière)' : ''}</td>
            <td className="pr-2">{r.ingredient}{r.warning && <span className="block text-xs text-[#C86B51]">{r.warning}</span>}</td><td className="pr-2 text-xs text-[#5C6A61]">{r.source_text}</td><td className="pr-2">{r.original}</td><td className="pr-2">× {r.coefficient}</td><td className="font-semibold">{r.result}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function QuantityCheck() {
  const [data, setData] = useState(null);
  const [validation, setValidation] = useState(null);

  const load = () => adminApi.getQuantityAffected().then((r) => setData(r.data)).catch(() => toast.error('Chargement impossible'));
  useEffect(() => { load(); }, []);

  const toggle = async (enable) => {
    if (enable && !window.confirm('Activer le nouveau calcul pour les fiches cuisiniers et les listes de courses ?')) return;
    await adminApi.setQuantityEngineV2(enable);
    toast.success(enable ? 'Nouveau calcul activé' : 'Ancien calcul rétabli');
    load();
  };

  const [audit, setAudit] = useState(null);
  const runAudit = () => adminApi.getRecipeAudit().then((r) => setAudit(r.data)).catch(() => toast.error('Analyse impossible'));

  const openValidation = (id) => adminApi.getQuantityValidation(id).then((r) => setValidation(r.data)).catch(() => toast.error('Calcul impossible'));

  if (!data) return <Loader2 className="w-6 h-6 animate-spin text-[#A5BBA8]" />;
  return (
    <div className="space-y-6" data-testid="quantity-check">
      <div className="card-default flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-[#2A312C]">Contrôle des quantités</h1>
          <p className="text-sm text-[#5C6A61]">Aperçu des fiches recalculées depuis les recettes d'origine (aucune réservation, recette ou paiement n'est modifié).</p>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={data.engine_v2_enabled} onCheckedChange={toggle} data-testid="qty-engine-toggle" />
          <span className="text-sm">{data.engine_v2_enabled ? 'Nouveau calcul actif partout' : 'Nouveau calcul en aperçu uniquement'}</span>
        </div>
      </div>
      <div className="card-default space-y-3" data-testid="qty-audit">
        <div className="flex items-center justify-between gap-3">
          <p className="font-medium text-[#2A312C]">Analyse de toutes les recettes (lecture seule)</p>
          <Button variant="outline" onClick={runAudit} data-testid="qty-audit-btn">Analyser les recettes</Button>
        </div>
        {audit && (audit.count === 0 ? <p className="text-sm text-[#5C6A61]">Aucune anomalie détectée.</p> : audit.recipes_with_issues.map((r) => (
          <div key={r.recipe_id} className="text-sm" data-testid={`qty-audit-${r.recipe_id}`}>
            <p className="font-semibold text-[#2A312C]">{r.name}</p>
            {r.issues.map((i, k) => <p key={k} className="text-[#C86B51] ml-3">{i.text} — {i.warning}</p>)}
          </div>
        )))}
      </div>
      <h2 className="text-lg font-semibold text-[#2A312C]">Prestations futures concernées ({data.reservations.length})</h2>
      {data.reservations.map((r) => (
        <div key={r.reservation_id} className="card-default flex flex-col md:flex-row md:items-center justify-between gap-3" data-testid={`qty-res-${r.reservation_id}`}>
          <div>
            <p className="font-medium text-[#2A312C]">{r.date} — {r.client_name || 'Client'}</p>
            <p className="text-sm text-[#5C6A61]">{r.formula} · {r.household_size} pers. · {r.reservation_id}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => openValidation(r.reservation_id)} data-testid={`qty-validate-${r.reservation_id}`}><ListChecks className="w-4 h-4 mr-2" />Contrôle des calculs</Button>
            <Button asChild className="bg-[#2A312C] hover:bg-[#3a443d]">
              <a href={`${API}/api/admin/reservations/${r.reservation_id}/fiche-pdf`} target="_blank" rel="noopener noreferrer" data-testid={`qty-pdf-${r.reservation_id}`}><FileText className="w-4 h-4 mr-2" />Fiche corrigée (PDF)</a>
            </Button>
          </div>
        </div>
      ))}
      <Dialog open={!!validation} onOpenChange={(o) => !o && setValidation(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Contrôle des calculs — {validation?.household} personne(s)</DialogTitle></DialogHeader>
          {validation && <ValidationTable v={validation} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
